/**
 * Batch 18-D5 — Book / Deep-Dive Time Modifier.
 *
 * Pure helper that computes a multiplicative score modifier for
 * long-form training content (books, e-books, course modules, long
 * videos) based on whether the technician is currently on the clock.
 * The recommendation engine multiplies the result into the final
 * artifact score via `ScoredArtifact.factors.bookTimeModifier`.
 *
 * Behavior is preserved BIT-FOR-BIT from
 * `src/utils/trainingRecommendationEngine.ts` prior to Batch 18-D5:
 *
 *   - `BOOK_TYPES`         = ['book', 'ebook']  (moved verbatim)
 *   - `DEEP_DIVE_TYPES`    = ['book', 'ebook', 'course_module', 'long_video']  (moved verbatim)
 *
 * Modifier matrix (priority order, top branch wins):
 *
 *   1. artifact is neither book nor deep-dive ............ 1.0
 *   2. manager-assigned OR saved ......................... 1.2
 *   3. work hours: book → 0.5, deep-dive → 0.7
 *   4. off-shift (anything else above didn't fire) ....... 1.0
 *
 * The manager-assigned / saved branch SHORT-CIRCUITS the work-hours
 * gate — a manager-assigned book at 10am still gets 1.2x, not 0.5x.
 * Today the engine only ever calls this helper with the first two
 * arguments (`isManagerAssigned` and `isSaved` default to `false` at
 * every live call site). The defaulted parameters are preserved so
 * future surfaces (e.g. saved-for-later UX) can opt into the boost
 * without re-shaping the function signature.
 *
 * The scaffold's `TrainingBookArtifactType` and
 * `TrainingDeepDiveArtifactType` (added in 18-D1) model the same
 * sets at the TYPE level. They are NOT used to retype the constants
 * here: the live call site uses `BOOK_TYPES.includes(artifact.artifact_type)`
 * where `artifact.artifact_type: ArtifactType` (wider), and TypeScript's
 * `Array#includes` would reject the wider argument against a narrowed
 * element type. Keeping `ArtifactType[]` preserves the existing call
 * shape; the scaffold types remain available for future declarative
 * uses.
 */

import type { ArtifactType } from '@/types/training';

// ─────────────────────────────────────────────────────────────────────
// Input shape
//
// Narrowed to exactly the field the helper reads. Any wider type that
// includes `artifact_type` (e.g. the engine's private
// `ArtifactWithDomains`) is assignable via structural typing.
// ─────────────────────────────────────────────────────────────────────

export interface BookTimeArtifactInput {
  artifact_type: ArtifactType;
}

// ─────────────────────────────────────────────────────────────────────
// Bucket constants
//
// Moved verbatim from the engine. Both arrays are intentionally
// duplicated (book ⊂ deep-dive) so the priority gate ("book → 0.5,
// deep-dive → 0.7") can distinguish them in O(1) without further
// computation.
// ─────────────────────────────────────────────────────────────────────

export const BOOK_TYPES: ArtifactType[] = ['book', 'ebook'];
export const DEEP_DIVE_TYPES: ArtifactType[] = ['book', 'ebook', 'course_module', 'long_video'];

// ─────────────────────────────────────────────────────────────────────
// Helper
//
// Body is verbatim from the engine.
// ─────────────────────────────────────────────────────────────────────

/**
 * Get time-based modifier for books and deep-dive content
 * - During work hours: 0.5x (suppress books)
 * - Off-shift: 1.0x (normal)
 * - Manager-assigned or saved: 1.2x (boost)
 */
export function getBookTimeModifier(
  artifact: BookTimeArtifactInput,
  isWorkHours: boolean,
  isManagerAssigned: boolean = false,
  isSaved: boolean = false
): number {
  const isDeepDive = DEEP_DIVE_TYPES.includes(artifact.artifact_type);
  const isBook = BOOK_TYPES.includes(artifact.artifact_type);

  if (!isDeepDive && !isBook) {
    return 1.0; // No modifier for non-book content
  }

  if (isManagerAssigned || isSaved) {
    return 1.2; // Boost for intentional learning
  }

  if (isWorkHours) {
    return isBook ? 0.5 : 0.7; // Suppress during work hours
  }

  return 1.0; // Normal off-shift
}
